import { useCallback, useEffect, useState } from "react";
import {
  getActiveLiveTrainingContext,
  resolveGroupIdsForLiveTraining,
} from "./useLiveTrainingRedirect";

function readSelectedSessionId() {
  try {
    return JSON.parse(localStorage.getItem("rsessn") || "null")?.ts_id || null;
  } catch {
    return null;
  }
}

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

const REFRESH_EVENTS = [
  "sessionSelected",
  "sessionCreated",
  "smarttt-session-changed",
  "smarttt-session-live-status",
  "smarttt-home-role-changed",
  "storage",
];

/**
 * Coach bottom-nav Planning / Training visibility.
 * Planning needs a created or selected session; Training also appears when
 * one of the coach's sessions is about to start (same window as auto-enter).
 */
export function useCoachTrainingNav({ enabled = true, pathname = "" } = {}) {
  const [selectedSessionId, setSelectedSessionId] = useState(() =>
    enabled ? readSelectedSessionId() : null
  );
  const [upcomingSessionId, setUpcomingSessionId] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setSelectedSessionId(null);
      setUpcomingSessionId(null);
      return;
    }
    setSelectedSessionId(readSelectedSessionId());
    const user = readUser();
    if (!user) {
      setUpcomingSessionId(null);
      return;
    }
    try {
      const groupIds = await resolveGroupIdsForLiveTraining(user);
      const ctx = await getActiveLiveTrainingContext({
        user,
        groupIds,
        permissions: user.permissions || [],
      });
      setUpcomingSessionId(ctx?.session?.ts_id || null);
    } catch {
      setUpcomingSessionId(null);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 60000);
    return () => window.clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (enabled) setSelectedSessionId(readSelectedSessionId());
  }, [enabled, pathname]);

  useEffect(() => {
    REFRESH_EVENTS.forEach((name) => window.addEventListener(name, refresh));
    return () => {
      REFRESH_EVENTS.forEach((name) => window.removeEventListener(name, refresh));
    };
  }, [refresh]);

  const hasSession = Boolean(selectedSessionId);
  return {
    showPlanning: Boolean(enabled && (hasSession || pathname.startsWith("/planning"))),
    showTraining: Boolean(
      enabled &&
        (hasSession ||
          upcomingSessionId ||
          pathname.startsWith("/live-training") ||
          pathname.startsWith("/training"))
    ),
  };
}
